import { faPencil, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, Grid, TextField } from "@mui/material";
import { isEqual } from "lodash";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EmployeesApiService } from "src/services/employees/employees-api.service";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { ActionButton, DialogContent } from "./styles";
import ConfirmationChangesDialog from "../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import { PropsFilter } from "../../../../../models/api/props-filter/props-filter";
import { ExchangeRatesFilter } from "../../../../../models/exchange-rates";
import { RevenueExpenseRef } from "../../../../../models/revenue-expense";
import AutocompleteDropdown from "../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { TextFieldTypeAmount } from "../../../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";
import { CurrenciesApiService } from "../../../../../services/currencies/currencies-api.service";
import { ExchangeRatesApiService } from "../../../../../services/exchange-rates";
import { LegalEntityProjectsApiService } from "../../../../../services/legal-entity-projects/legal-entity-projects-api.service";
import { RevenueExpenseTypesApiService } from "../../../../../services/revenue-expense-type/revenue-expense-type-api.service";
import DialogActions from "../../../../DialogActions/DialogActions";
import DialogTitle from "../../../../DialogTitle/DialogTitle";
import ExchangeRateBookingsSuggestions from "../../ExchangeRateBookingsSuggestions/ExchangeRateBookingsSuggestions";

const AddEditRevenueRow = ({ handleChangeRevenue, legalEntity, item, currency, date }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_transaction.transaction_screen_two.revenue"; // tRP - translation root path

  const currCompanyAccount = useAppSelector((state) => state.currentUserReducer.currentCompanyAccount);

  const DEFAULT_ITEM: Partial<RevenueExpenseRef> = useMemo(() => {
    return {
      legalEntity: item?.legalEntity ?? legalEntity ?? null,
      currency: item?.currency ?? currency ?? null,
      bookedCurrency: item?.bookedCurrency ?? currency ?? null,
      revenueExpenseType: item?.revenueExpenseType ?? null,
      employee: item?.employee ?? null,
      project: item?.project ?? null,
      transaction: item?.transaction ?? null,
      amount: item?.amount ?? 0,
      exchangeRate: item?.exchangeRate ?? 1,
      bookedAmount: item?.bookedAmount ?? 0,
    };
  }, [legalEntity, item]);

  //#region initial state
  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      revenue: item ?? (DEFAULT_ITEM as RevenueExpenseRef),
      exchangeRateSuggestions: [],
      isCCDOpen: false,
      areFieldsValidated: !!item,
    };
  }, [DEFAULT_ITEM, item]);
  //#endregion

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region handle change revenue field
  const handleChangeRevenueField = useCallback(
    (field: keyof RevenueExpenseRef, value: RevenueExpenseRef[keyof RevenueExpenseRef]) => {
      if (field === "bookedAmount") {
        const exchangeRate =
          !value || !state.revenue.amount ? "0" : ((value as number) / state.revenue.amount).toFixed(4);

        setState((state) => ({
          ...state,
          revenue: { ...state.revenue, [field]: value as number, exchangeRate: parseFloat(exchangeRate) },
        }));
      } else if (field === "amount" || field === "exchangeRate") {
        const bookedAmount = !value
          ? "0"
          : field === "amount"
          ? ((value as number) * state.revenue.exchangeRate).toFixed(2)
          : ((value as number) * state.revenue.amount).toFixed(2);

        setState((state) => ({
          ...state,
          revenue: { ...state.revenue, [field]: value, bookedAmount: parseFloat(bookedAmount) },
        }));
      } else {
        setState((state) => ({
          ...state,
          revenue: { ...state.revenue, [field]: value },
        }));
      }
    },
    [state.revenue]
  );
  //#endregion

  //#region handle confirm
  const handleConfirm = useCallback(async () => {
    handleChangeRevenue({
      ...state.revenue,
      id: item ? item.id : uuidv4(),
      companyAccount: item ? item.companyAccount : currCompanyAccount,
    });

    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE, item, handleChangeRevenue, state.revenue, currCompanyAccount]);

  //#region discard changes
  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);
  //#endregion

  //#region reset and close
  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.revenue, INITIAL_STATE.revenue)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };
  //#endregion

  useEffect(() => {
    !item &&
      setState((state) => ({
        ...state,
        revenue: {
          ...state.revenue,
          legalEntity: legalEntity ?? null,
          currency: currency ?? null,
          bookedCurrency: currency ?? null,
        },
      }));
  }, [item, legalEntity, currency]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        revenue: item,
      }));

    return () => {
      if (!item) setState({ ...INITIAL_STATE });
    };
  }, [item, INITIAL_STATE]);

  //#region load exchange rates
  const loadExchangeRates = useCallback(
    async (signal?: AbortSignal) => {
      const apiService = new ExchangeRatesApiService();
      apiService.useLoadItemsPath("/GetSuggestedExchangeRate");

      try {
        const response = await apiService.loadItems(
          {
            sourceId: state.revenue?.currency?.id,
            destinationId: state.revenue?.bookedCurrency?.id,
            year: new Date(date).getFullYear(),
            month: new Date(date).getMonth() + 1,
            legalEntityId: state.revenue?.legalEntity?.id,
          } as ExchangeRatesFilter,
          undefined,
          undefined,
          undefined,
          undefined,
          60,
          signal
        );

        if (response.error || signal?.aborted || !signal) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        } else {
          setState((state) => ({
            ...state,
            revenue: {
              ...state.revenue,
              ...(!item && {
                exchangeRate: response.items[0]?.rate ?? 1,
                bookedAmount:
                  response.items[0]?.rate && !!state.revenue.amount
                    ? parseFloat((response.items[0].rate * state.revenue.amount).toFixed(2))
                    : state.revenue.bookedAmount,
              }),
            },
            exchangeRateSuggestions: response.items,
          }));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [date, state.revenue?.bookedCurrency, state.revenue?.currency, state.revenue?.legalEntity]
  );
  //#endregion

  useEffect(() => {
    const abortController = new AbortController();

    state.isDialogOpen &&
      !!state.revenue?.legalEntity &&
      !!date &&
      !!state.revenue?.bookedCurrency &&
      !!state.revenue?.currency &&
      loadExchangeRates(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [date, state.revenue?.bookedCurrency, state.revenue?.currency, state.revenue?.legalEntity, state.isDialogOpen]);

  //#region are fields validated
  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.revenue.revenueExpenseType &&
      !!state.revenue.amount &&
      !!state.revenue.bookedAmount &&
      !!state.revenue.currency &&
      !!state.revenue.bookedCurrency &&
      !!state.revenue.exchangeRate
    );
  }, [state.revenue]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.revenue, areFieldsValidated]);
  //#endregion

  return (
    <>
      {item ? (
        <ActionButton
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
        >
          <FontAwesomeIcon icon={faPencil} />
        </ActionButton>
      ) : (
        <Button
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
        >
          <FontAwesomeIcon
            icon={faPlusCircle}
            style={{ width: "25px", height: "25px" }}
          />
        </Button>
      )}
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
      >
        <DialogTitle
          title={item ? t(`${tRP}.edit_title`) : t(`${tRP}.title`)}
          handleClose={resetAndCloseDialog}
        />
        <DialogContent>
          <AutocompleteDropdown
            labelKey={`${t(`${tRP}.fields.revenue_type`)}`}
            propsItem={state.revenue}
            field="revenueExpenseType"
            serviceApi={new RevenueExpenseTypesApiService()}
            handleChange={handleChangeRevenueField}
            propsFilter={{ isExpense: false } as PropsFilter}
            required
          />
          <AutocompleteDropdown
            labelKey={`${t(`${tRP}.fields.employee`)}`}
            propsItem={state.revenue}
            field="employee"
            serviceApi={new EmployeesApiService()}
            handleChange={handleChangeRevenueField}
            propsFilter={
              {
                legalEntityIds: legalEntity?.id ? [legalEntity?.id] : [],
              } as PropsFilter
            }
          />
          <AutocompleteDropdown
            labelKey={`${t(`${tRP}.fields.project`)}`}
            propsItem={state.revenue}
            field="project"
            serviceApi={new LegalEntityProjectsApiService()}
            handleChange={handleChangeRevenueField}
            propsFilter={
              {
                legalEntityIds: legalEntity?.id ? [legalEntity?.id] : [],
              } as PropsFilter
            }
          />

          <Grid
            sx={{
              width: "45%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",

              ".MuiAutocomplete-root, & > .MuiFormControl-root": {
                width: "100%%",
              },
            }}
          >
            <TextFieldTypeAmount
              field="amount"
              labelKey={`${t(`${tRP}.fields.amount`)}`}
              handleChange={(_, number) => handleChangeRevenueField("amount", number)}
              amountValue={state.revenue.amount ?? 0}
              sxProps={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              endAdornment={
                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.currency`)}`}
                  propsItem={state.revenue}
                  field={"currency"}
                  handleChange={handleChangeRevenueField}
                  serviceApi={new CurrenciesApiService()}
                  noLabel={true}
                  inputStyle={{
                    padding: "1px 46px 1px 1px",
                    width: "100px",
                  }}
                  fieldProp={"code"}
                />
              }
            />

            {/* <TextField
              label={`${t(`${tRP}.fields.amount`)}`}
              onChange={(e) => handleChangeRevenueField("amount", e.target.value)}
              type="number"
              value={state.revenue.amount}
              sx={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <AutocompleteDropdown
                    labelKey={`${t(`${tRP}.fields.currency`)}`}
                    propsItem={state.revenue}
                    field={"currency"}
                    handleChange={handleChangeRevenueField}
                    serviceApi={new CurrenciesApiService()}
                    noLabel={true}
                    inputStyle={{
                      padding: "1px 46px 1px 1px",
                      width: "100px",
                    }}
                    fieldProp={"code"}
                    disabled
                  />
                ),
              }}
            /> */}

            <TextField
              label={`${t(`${tRP}.fields.exchange_rate`)}`}
              type="number"
              onChange={(event) => {
                handleChangeRevenueField("exchangeRate", event.currentTarget.value);
              }}
              value={state.revenue.exchangeRate}
              disabled={isEqual(state.revenue?.currency?.id, state.revenue?.bookedCurrency?.id)}
              InputProps={{
                endAdornment: (
                  <ExchangeRateBookingsSuggestions exchangeRateSuggestions={state.exchangeRateSuggestions} />
                ),
              }}
            />

            <TextFieldTypeAmount
              field="bookedAmount"
              labelKey={`${t(`${tRP}.fields.converted_amount`)}`}
              handleChange={(_, number) => handleChangeRevenueField("bookedAmount", number)}
              amountValue={state.revenue.bookedAmount ?? 0}
              sxProps={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              endAdornment={
                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.currency`)}`}
                  propsItem={state.revenue}
                  field={"bookedCurrency"}
                  handleChange={handleChangeRevenueField}
                  serviceApi={new CurrenciesApiService()}
                  noLabel={true}
                  inputStyle={{
                    padding: "1px 46px 1px 1px",
                    width: "100px",
                  }}
                  fieldProp={"code"}
                />
              }
            />
            {/* <TextField
              label={`${t(`${tRP}.fields.converted_amount`)}`}
              type="number"
              value={state.revenue.bookedAmount}
              onChange={(e) => handleChangeRevenueField("bookedAmount", e.target.value)}
              sx={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <>
                    <AutocompleteDropdown
                      labelKey={`${t(`${tRP}.fields.currency`)}`}
                      propsItem={state.revenue}
                      field={"bookedCurrency"}
                      handleChange={handleChangeRevenueField}
                      serviceApi={new CurrenciesApiService()}
                      noLabel={true}
                      inputStyle={{
                        padding: "1px 46px 1px 1px",
                        width: "100px",
                      }}
                      fieldProp={"code"}
                    />
                  </>
                ),
              }}
            /> */}
          </Grid>
        </DialogContent>
        <DialogActions
          handleCancel={resetAndCloseDialog}
          handleSave={handleConfirm}
          customSaveName={`${t("buttons.confirm")}`}
          disabledSaveButton={!areFieldsValidated()}
        />
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AddEditRevenueRow;
